import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  useCorrespondencePackageSendMutation,
} from '@/services/api/correspondenceService';
import { usePreparePremisesCertifiedMailMutation } from '@/services/api/coverageService';
import {
  CoverageCategory,
  type CoverageSchema,
  type PropertyContact,
} from '@/services/types/coverage-types';

export function usePremisesDelivery({
  coverage,
  matterId,
  coverageId,
  selectedDocId,
  savedDeliveryRecipient,
  refetchCoverage,
  refetchTasks,
  taskId,
}: {
  coverage: CoverageSchema | undefined;
  matterId: string | undefined;
  coverageId: string | undefined;
  selectedDocId: string | null;
  savedDeliveryRecipient: string;
  refetchCoverage: () => void;
  refetchTasks: () => void;
  taskId: string | undefined;
}) {
  const isPremisesCoverage =
    coverage?.category === CoverageCategory.PROPERTY;

  // --- State ---
  const [selectedPremisesContactIdx, setSelectedPremisesContactIdx] =
    useState<number | null>(null);
  const [isPreparingCertifiedMail, setIsPreparingCertifiedMail] =
    useState(false);
  const [isSendingCertifiedMail, setIsSendingCertifiedMail] =
    useState(false);
  const [certifiedMailSendStatus, setCertifiedMailSendStatus] = useState<
    'pending' | 'sending' | 'sent' | null
  >(null);

  // --- Mutations & queries ---
  const [preparePremisesCertifiedMail] =
    usePreparePremisesCertifiedMailMutation();
  const [sendCorrespondencePackage] =
    useCorrespondencePackageSendMutation();

  // --- Derived data ---
  const propertyContacts = useMemo(() => {
    if (!isPremisesCoverage) return [];
    return (coverage?.property_contacts ?? []) as PropertyContact[];
  }, [isPremisesCoverage, coverage?.property_contacts]);

  // Auto-select a contact based on saved delivery recipient or first mailable
  useEffect(() => {
    if (!isPremisesCoverage || propertyContacts.length === 0) return;
    if (selectedPremisesContactIdx !== null) return;

    if (savedDeliveryRecipient.trim()) {
      const recipients = savedDeliveryRecipient
        .split(',')
        .map((r) => r.trim());
      const firstRecipient = recipients[0];
      if (firstRecipient) {
        const matchIdx = propertyContacts.findIndex((contact) =>
          contact.handles?.some((h) => h.value === firstRecipient),
        );
        if (matchIdx >= 0) {
          setSelectedPremisesContactIdx(matchIdx);
          return;
        }
      }
    }
    const firstMailableIdx = propertyContacts.findIndex((contact) =>
      Boolean(contact.mailing_address),
    );
    setSelectedPremisesContactIdx(
      firstMailableIdx >= 0 ? firstMailableIdx : 0,
    );
  }, [
    isPremisesCoverage,
    propertyContacts,
    savedDeliveryRecipient,
    selectedPremisesContactIdx,
  ]);

  const selectedPremisesContact = useMemo(() => {
    if (!isPremisesCoverage || selectedPremisesContactIdx === null)
      return null;
    return propertyContacts[selectedPremisesContactIdx] ?? null;
  }, [isPremisesCoverage, propertyContacts, selectedPremisesContactIdx]);

  const premisesEmailRecipients = useMemo(() => {
    if (!selectedPremisesContact) return [];
    const handles = selectedPremisesContact.handles ?? [];
    const emailHandle = handles.find((h) =>
      (h.handle_type || '').toUpperCase().includes('EMAIL'),
    );
    const trimmed = emailHandle?.value?.trim();
    return trimmed ? [trimmed] : [];
  }, [selectedPremisesContact]);

  const premisesFaxNumber = useMemo(() => {
    if (!selectedPremisesContact) return '';
    const handles = selectedPremisesContact.handles ?? [];
    const faxHandle = handles.find((h) =>
      (h.handle_type || '').toUpperCase().includes('FAX'),
    );
    return faxHandle?.value ?? '';
  }, [selectedPremisesContact]);

  // --- Certified mail send handler ---
  const handleSendCertifiedMail = useCallback(async () => {
    if (!matterId || !coverageId) {
      toast.error('Missing matter or coverage context for certified mail.');
      return;
    }
    if (!taskId) {
      toast.error('Missing Send LOR task context for certified mail.');
      return;
    }
    if (selectedPremisesContactIdx === null) {
      toast.error('Select a property contact with a mailing address.');
      return;
    }

    const selectedPropertyContact =
      propertyContacts[selectedPremisesContactIdx] ?? null;

    if (
      !selectedPropertyContact?.id ||
      !selectedPropertyContact?.name?.trim() ||
      !selectedPropertyContact.mailing_address
    ) {
      toast.error(
        'The selected property contact needs an id, name, and valid mailing address.',
      );
      return;
    }

    setIsPreparingCertifiedMail(true);
    let preparedPackage;
    try {
      preparedPackage = await preparePremisesCertifiedMail({
        matterId,
        coverageId,
        taskId,
        createNewAttempt: false,
        lorDocumentId:
          selectedDocId ?? coverage?.lor_document_id ?? undefined,
        contactId: selectedPropertyContact.id,
        contactIndex: selectedPremisesContactIdx,
      }).unwrap();
    } finally {
      setIsPreparingCertifiedMail(false);
    }

    setIsSendingCertifiedMail(true);
    try {
      const sendResult = await sendCorrespondencePackage({
        matterId,
        id: String(preparedPackage.id),
        sendPackageRequest: {
          package_id: String(preparedPackage.id),
        },
      }).unwrap();
      const CERTIFIED_MAIL_STATUS_MAP: Record<
        string,
        'sent' | 'sending' | 'pending'
      > = {
        sent: 'sent',
        sending: 'sending',
      };
      const nextStatus =
        CERTIFIED_MAIL_STATUS_MAP[sendResult.status] ?? 'pending';
      setCertifiedMailSendStatus(nextStatus);
      await refetchCoverage();
      await refetchTasks();
      toast.success(
        nextStatus === 'sent'
          ? 'Certified mail sent'
          : 'Certified mail queued for sending',
      );
    } finally {
      setIsSendingCertifiedMail(false);
    }
  }, [
    matterId,
    coverageId,
    taskId,
    selectedPremisesContactIdx,
    propertyContacts,
    preparePremisesCertifiedMail,
    selectedDocId,
    coverage?.lor_document_id,
    sendCorrespondencePackage,
    refetchCoverage,
    refetchTasks,
  ]);

  return {
    isPremisesCoverage,
    propertyContacts,
    selectedPremisesContactIdx,
    setSelectedPremisesContactIdx,
    selectedPremisesContact,
    premisesEmailRecipients,
    premisesFaxNumber,
    isPreparingCertifiedMail,
    isSendingCertifiedMail,
    certifiedMailSendStatus,
    handleSendCertifiedMail,
  };
}
